const PAGE_WIDTH = 595
const PAGE_HEIGHT = 842

const escapePdfText = (value) =>
{
    return String(value)
        .replace(/\\/g, '\\\\')
        .replace(/\(/g, '\\(')
        .replace(/\)/g, '\\)')
}

const formatPrice = (value) =>
{
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0
    }).format(value)
}

const parseJpegSize = (bytes) =>
{
    let offset = 2

    while(offset < bytes.length)
    {
        if(bytes[offset] !== 0xFF)
        {
            offset += 1
            continue
        }

        const marker = bytes[offset + 1]
        const length = (bytes[offset + 2] << 8) | bytes[offset + 3]

        if(marker >= 0xC0 && marker <= 0xC3)
        {
            return {
                height: (bytes[offset + 5] << 8) | bytes[offset + 6],
                width: (bytes[offset + 7] << 8) | bytes[offset + 8]
            }
        }

        offset += 2 + length
    }

    throw new Error('Unable to parse JPEG dimensions.')
}

const dataUrlToBytes = (dataUrl) =>
{
    const base64 = dataUrl.split(',')[1]
    const binary = atob(base64)
    const bytes = new Uint8Array(binary.length)

    for(let i = 0; i < binary.length; i++)
    {
        bytes[i] = binary.charCodeAt(i)
    }

    return bytes
}

const buildTextCommands = (exportData, imageBox) =>
{
    const commands = []
    let y = imageBox.y - 26

    commands.push('BT /F1 24 Tf 50 804 Td (Work Desk) Tj ET')
    commands.push(`q ${imageBox.width} 0 0 ${imageBox.height} ${imageBox.x} ${imageBox.y} cm /Im1 Do Q`)

    const addSectionTitle = (title) =>
    {
        commands.push(`BT /F1 14 Tf 50 ${y} Td (${escapePdfText(title)}) Tj ET`)
        y -= 20
    }

    const addTableRows = (rows, leftWidth = 150) =>
    {
        for(const [label, value] of rows)
        {
            commands.push(`BT /F1 10 Tf 50 ${y} Td (${escapePdfText(label)}) Tj ET`)
            commands.push(`BT /F1 10 Tf ${50 + leftWidth} ${y} Td (${escapePdfText(value)}) Tj ET`)
            y -= 16
        }
    }

    addSectionTitle('Dimension Table')
    addTableRows(exportData.dimensions)
    y -= 8

    addSectionTitle('Selections')
    addTableRows(exportData.selections)
    y -= 8

    addSectionTitle('Price Breakdown')
    for(const item of exportData.lineItems)
    {
        commands.push(`BT /F1 10 Tf 50 ${y} Td (${escapePdfText(item.label)}) Tj ET`)
        commands.push(`BT /F1 10 Tf 430 ${y} Td (${escapePdfText(formatPrice(item.price))}) Tj ET`)
        y -= 16
    }

    y -= 8
    commands.push(`BT /F1 15 Tf 50 ${y} Td (Final Price) Tj ET`)
    commands.push(`BT /F1 15 Tf 430 ${y} Td (${escapePdfText(formatPrice(exportData.total))}) Tj ET`)

    return commands.join('\n')
}

export const exportDeskPdf = ({ exportData, heroImageDataUrl }) =>
{
    const imageBytes = dataUrlToBytes(heroImageDataUrl)
    const imageSize = parseJpegSize(imageBytes)
    const imageMaxWidth = PAGE_WIDTH - 100
    const imageMaxHeight = 250
    const imageScale = Math.min(imageMaxWidth / imageSize.width, imageMaxHeight / imageSize.height)
    const imageWidth = imageSize.width * imageScale
    const imageHeight = imageSize.height * imageScale
    const imageBox = {
        x: (PAGE_WIDTH - imageWidth) / 2,
        y: PAGE_HEIGHT - 60 - imageHeight,
        width: imageWidth,
        height: imageHeight
    }

    const contentStream = buildTextCommands(exportData, imageBox)
    const imageBinary = Array.from(imageBytes, (byte) => String.fromCharCode(byte)).join('')

    const objects = [
        '<< /Type /Catalog /Pages 2 0 R >>',
        '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
        '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 4 0 R >> /XObject << /Im1 5 0 R >> >> /Contents 6 0 R >>',
        '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>',
        `<< /Type /XObject /Subtype /Image /Width ${imageSize.width} /Height ${imageSize.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${imageBytes.length} >>\nstream\n${imageBinary}\nendstream`,
        `<< /Length ${contentStream.length} >>\nstream\n${contentStream}\nendstream`
    ]

    let pdf = '%PDF-1.4\n'
    const offsets = [0]

    objects.forEach((object, index) =>
    {
        offsets.push(pdf.length)
        pdf += `${index + 1} 0 obj\n${object}\nendobj\n`
    })

    const xrefOffset = pdf.length
    pdf += `xref\n0 ${objects.length + 1}\n`
    pdf += '0000000000 65535 f \n'

    for(let i = 1; i <= objects.length; i++)
    {
        pdf += `${String(offsets[i]).padStart(10, '0')} 00000 n \n`
    }

    pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`

    const pdfBytes = new Uint8Array(pdf.length)

    for(let i = 0; i < pdf.length; i++)
    {
        pdfBytes[i] = pdf.charCodeAt(i) & 0xFF
    }

    const blob = new Blob([pdfBytes], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'work-desk-configuration.pdf'
    link.click()
    URL.revokeObjectURL(url)
}
